/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef } from "react";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { accessAPI, tituloBase, Titulo, useIva } from "../../../shared";
import FiltrosIndicadores from "./FiltrosIndicadores";
import TarjetasResumen from "./TarjetasResumen";
import GraficaFacturacion from "./GraficaFacturacion";
import TablaDetalle from "./TablaDetalle";
import { rangoDePeriodo, sumarDias } from "./periodos";
import { filtrarItems, itemsEnMoneda, resumen, SIN_TIPO } from "./calculos";

// Opciones de un filtro armadas con los valores que aparecen en los datos,
// ordenadas por nombre.
function opcionesDe(items, clave, nombre) {
  const porId = new Map();
  for (const i of items) {
    if (i[clave] != null && !porId.has(i[clave])) {
      porId.set(i[clave], { id: i[clave], nombre: i[nombre] });
    }
  }
  return [...porId.values()].sort((a, b) =>
    (a.nombre ?? "").localeCompare(b.nombre ?? "")
  );
}

export default function IndicadoresFacturacion() {
  const [filtros, setFiltros] = useState({
    periodo: "anio",
    desde: null,
    hasta: null,
    monedaId: null,
    clienteId: null,
    certificadoTipoId: null,
    mostrarConIva: false
  });
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const toast = useRef(null);
  const iva = useIva();

  const rango = useMemo(
    () =>
      rangoDePeriodo(filtros.periodo, {
        desde: filtros.desde,
        hasta: filtros.hasta
      }),
    [filtros.periodo, filtros.desde, filtros.hasta]
  );

  useEffect(() => {
    document.title = "Indicadores de facturación";
    return () => (document.title = tituloBase());
  }, []);

  // La API filtra por fecha UTC y el panel por fecha local, así que se le pide
  // un día de margen a cada lado; el recorte exacto lo hacen los cálculos.
  useEffect(() => {
    if (!rango) return;
    setCargando(true);
    const params = new URLSearchParams({
      desde: sumarDias(rango.desde, -1),
      hasta: sumarDias(rango.hasta, 1)
    });
    accessAPI(
      "GET",
      `admin/indicadores/facturacion?${params}`,
      null,
      (respuesta) => {
        setDatos(respuesta);
        setFiltros((f) =>
          f.monedaId != null &&
          respuesta.monedas.some((m) => m.id === f.monedaId)
            ? f
            : {
                ...f,
                monedaId:
                  (respuesta.monedas.find((m) => m.principal) ?? respuesta.monedas[0])
                    ?.id ?? null
              }
        );
        setCargando(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar los indicadores",
          sticky: true
        });
        setCargando(false);
      }
    );
  }, [rango]);

  const cambiarFiltro = (clave, valor) =>
    setFiltros((f) => {
      const nuevos = { ...f, [clave]: valor };
      // Al pasar a personalizado se arranca del rango que se estaba viendo.
      if (clave === "periodo" && valor === "personalizado" && rango) {
        nuevos.desde = f.desde || rango.desde;
        nuevos.hasta = f.hasta || rango.hasta;
      }
      return nuevos;
    });

  // Las facturas de la moneda elegida. En la principal entran todas, llevadas
  // a esa moneda con su tipo de cambio.
  const facturasEnMoneda = useMemo(
    () =>
      datos
        ? itemsEnMoneda(
            datos.facturas,
            filtros.monedaId,
            datos.monedas.find((m) => m.principal)?.id
          )
        : [],
    [datos, filtros.monedaId]
  );

  // Las opciones de cliente y tipo de certificado salen de los datos de la
  // moneda elegida, para no ofrecer filtros que dejan todo vacío.
  const { clientes, certificadoTipos } = useMemo(() => {
    const items = facturasEnMoneda;
    const tipos = opcionesDe(items, "certificadoTipoId", "certificadoTipoNombre");
    if (items.some((i) => i.certificadoTipoId == null)) {
      tipos.push({ id: SIN_TIPO, nombre: "Sin tipo de certificado" });
    }
    return {
      clientes: opcionesDe(items, "clienteId", "clienteNombre"),
      certificadoTipos: tipos
    };
  }, [facturasEnMoneda]);

  const opciones = useMemo(
    () => ({ mostrarConIva: filtros.mostrarConIva, iva }),
    [filtros.mostrarConIva, iva]
  );

  const filtrados = useMemo(() => {
    if (!datos || filtros.monedaId == null) return null;
    return { facturas: filtrarItems(facturasEnMoneda, filtros) };
  }, [datos, facturasEnMoneda, filtros]);

  const totales = useMemo(
    () =>
      filtrados && rango
        ? resumen(filtrados.facturas, rango, opciones)
        : null,
    [filtrados, rango, opciones]
  );

  const moneda = datos?.monedas.find((m) => m.id === filtros.monedaId);

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo>Indicadores de facturación</Titulo>

      <FiltrosIndicadores
        filtros={filtros}
        onCambio={cambiarFiltro}
        monedas={datos?.monedas || []}
        clientes={clientes}
        certificadoTipos={certificadoTipos}
      />

      {cargando || !rango ? (
        !rango ? null : (
          <Skeleton width="100%" height="8rem" borderRadius="6px" />
        )
      ) : !totales ? (
        <div css={{ textAlign: "center", padding: "2em" }}>
          No hay facturación registrada.
        </div>
      ) : (
        <>
          <TarjetasResumen
            totales={totales}
            simbolo={moneda?.simbolo || ""}
            mostrarConIva={filtros.mostrarConIva}
          />
          <GraficaFacturacion
            facturas={filtrados.facturas}
            rango={rango}
            opciones={opciones}
            simbolo={moneda?.simbolo || ""}
          />
          <TablaDetalle
            facturas={filtrados.facturas}
            rango={rango}
            opciones={opciones}
            simbolo={moneda?.simbolo || ""}
          />
        </>
      )}
    </>
  );
}
